import { trackEvent } from './mixpanel';
import { MIXPANEL_EVENT_PARENT, MIXPANEL_EVENTS } from './enums';
import { getCurrentPage, getDefaultLocale } from './genericHelpers';
import { variable_applied, variable_combination } from './constants';

const withLocale = extraProps => ({
  ...extraProps,
  locale: getDefaultLocale(),
});

export const trackSymptomClicked = (symptomId = null) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      MIXPANEL_EVENTS?.SYMPTOM_CLICKED,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.SYMPTOMS,
        symptomId: symptomId,
        sourcePage: currentPageName,
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackSymptomsFilter = (
  eventType = MIXPANEL_EVENTS?.SYMPTOM_FILTER_APPLIED,
  filterType = null,
  filterValue = null,
  filters = null
) => {
  const currentPageName = getCurrentPage();
  try {
    const props = {
      parent: MIXPANEL_EVENT_PARENT?.SYMPTOMS,
      sourcePage: currentPageName,
    };

    if (eventType === MIXPANEL_EVENTS?.SYMPTOM_FILTER_APPLIED && filterType && filterValue) {
      props.filterType = filterType;
      props.filterValue = filterValue;
    } else if (eventType === MIXPANEL_EVENTS?.SYMPTOM_FILTER_COMBINATION && filters) {
      props.filters = filters;
    }

    trackEvent(eventType, withLocale(props));
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackSessionsClicked = (
  eventType = MIXPANEL_EVENTS?.SESSION_CLICKED,
  sessionId = null,
  sessionName = null,
  category = null
) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.SESSIONS,
        sourcePage: currentPageName,
        ...(sessionId && { sessionId: sessionId }),
        ...(sessionName && { sessionName: sessionName }),
        ...(category && { category: category }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackJoinSessionsClicked = (sessionId = null, timeToStart = null) => {
  try {
    const currentPageName = getCurrentPage();
    trackEvent(
      MIXPANEL_EVENTS?.SESSION_JOIN_CLICKED,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.SESSIONS,
        sourcePage: currentPageName,
        ...(sessionId && { sessionId: sessionId }),
        ...(timeToStart && { timeToStart: timeToStart }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackSessionsFilter = (
  eventType = MIXPANEL_EVENTS?.SESSIONS_FILTER_APPLIED,
  filterType = null,
  filterValue = null,
  filters = null,
  date = null,
  weekdayWeekend = null
) => {
  const currentPageName = getCurrentPage();
  try {
    const props = {
      parent: MIXPANEL_EVENT_PARENT?.SESSIONS,
      sourcePage: currentPageName,
    };

    if (eventType === MIXPANEL_EVENTS?.SESSIONS_FILTER_APPLIED && filterType && filterValue) {
      props.filterType = filterType;
      props.filterValue = filterValue;
    } else if (eventType === MIXPANEL_EVENTS?.SESSIONS_FILTER_COMBINATION && filters) {
      props.filters = filters;
    } else if (eventType === MIXPANEL_EVENTS?.SESSION_DATE_SELECTED && date) {
      props.date = date;
      if (weekdayWeekend) {
        props['weekday/weekend'] = weekdayWeekend;
      }
    }

    trackEvent(eventType, withLocale(props));
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackQuickResourceClicked = (resourceId = null, category = null) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      MIXPANEL_EVENTS?.QUICK_RESOURCE_CLICKED,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.QUICK_RESOURCES,
        sourcePage: currentPageName,
        ...(resourceId && { resourceId: resourceId }),
        ...(category && { category: category }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackWellnessProgramClicked = (
  eventType = MIXPANEL_EVENTS?.PROGRAM_CLICKED,
  programId = null,
  programTitle = null
) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.PROGRAMS,
        sourcePage: currentPageName,
        ...(programId && { programId: programId }),
        ...(programTitle && { programTitle: programTitle }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackWellnessProgramEnrolled = (
  eventType = MIXPANEL_EVENTS?.PROGRAM_ENROLLED,
  programId = null,
  programTitle = null,
  programLength = null
) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.PROGRAMS,
        sourcePage: currentPageName,
        ...(programId && { programId: programId }),
        ...(programTitle && { programTitle: programTitle }),
        ...(programLength && { programLength: programLength }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackWellnessProgramUnroll = (
  programId = null,
  programTitle = null,
  programLength = null,
  daysCompleted = null,
  daysActive = null
) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      MIXPANEL_EVENTS?.PROGRAM_UN_ENROLLED,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.PROGRAMS,
        sourcePage: currentPageName,
        ...(programId && { programId: programId }),
        ...(programTitle && { programTitle: programTitle }),
        ...(programLength && { programLength: programLength }),
        ...(daysCompleted && { daysCompleted: daysCompleted }),
        ...(daysActive && { daysActive: daysActive }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackWellnessProgramTaskEvent = (
  eventType,
  programId = null,
  programTitle = null,
  programLength = null,
  taskId = null,
  taskTitle = null,
  taskHasArticle = null,
  taskHasVideo = null,
  taskHasOptions = null,
  articleTitle = null,
  videoTitle = null,
  videoWatchTime = null,
  videoTotalTime = null
) => {
  try {
    const props = {
      parent: MIXPANEL_EVENT_PARENT?.PROGRAMS,
      ...(programId && { programId: programId }),
      ...(programTitle && { programTitle: programTitle }),
      ...(programLength && { programLength: programLength }),
      ...(taskId && { taskId: taskId }),
      ...(taskTitle && { taskTitle: taskTitle }),
      ...(taskHasArticle && { taskHasArticle: taskHasArticle }),
      ...(taskHasVideo && { taskHasVideo: taskHasVideo }),
      ...(taskHasOptions && { taskHasOptions: taskHasOptions }),
      ...(articleTitle && { articleTitle: articleTitle }),
      ...(videoTitle && { videoTitle: videoTitle }),
      ...(videoWatchTime && { videoWatchTime: videoWatchTime }),
      ...(videoTotalTime && { videoTotalTime: videoTotalTime }),
    };

    trackEvent(eventType, withLocale(props));
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackModuleClicked = (moduleId = null, moduleTitle = null) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      MIXPANEL_EVENTS?.MODULE_CLICKED,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.VIDEOS,
        sourcePage: currentPageName,
        ...(moduleId && { moduleId: moduleId }),
        ...(moduleTitle && { moduleTitle: moduleTitle }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackVideoEvent = (eventName, props = {}) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventName,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.VIDEOS,
        sourcePage: currentPageName,
        ...props,
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackArticleCategoryClicked = (
  eventType = MIXPANEL_EVENTS?.ARTICLE_CATEGORY_CLICKED,
  category = null
) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.ARTICLES,
        sourcePage: currentPageName,
        ...(category && { category: category }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackArticleFilter = (
  prefix,
  eventSuffix = variable_applied,
  filterType = null,
  filterValue = null,
  filters = null
) => {
  const currentPageName = getCurrentPage();
  const eventType = `${prefix}_filter_${eventSuffix}`;

  try {
    const props = {
      parent: MIXPANEL_EVENT_PARENT?.ARTICLES,
    };

    if (eventSuffix === variable_applied && filterType && filterValue) {
      props.filterType = filterType;
      props.filterValue = filterValue;
    } else if (eventSuffix === variable_combination && filters) {
      props.filters = filters;
    }

    trackEvent(eventType, withLocale(props));
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackArticleEvent = (
  eventType,
  { articleId = null, articleTitle = null, sourceArticleId = null } = {}
) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.ARTICLES,
        sourcePage: currentPageName,
        ...(articleId && { articleId: articleId }),
        ...(articleTitle && { articleTitle: articleTitle }),
        ...(sourceArticleId && { sourceArticleId: sourceArticleId }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackAContactUsClicked = (eventType = MIXPANEL_EVENTS?.CONTACT_INFO_BUTTON) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.CONTACT,
        ...(currentPageName && { sourcePage: currentPageName }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackLogOutClicked = () => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      MIXPANEL_EVENTS?.LOGOUT_COMPLETED,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.LOGOUT,
        ...(currentPageName && { sourcePage: currentPageName }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackLogInClicked = (eventType = MIXPANEL_EVENTS?.LOGIN_OPENED) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.LOGIN,
        ...(currentPageName && { sourcePage: currentPageName }),
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackRegisterEvent = (eventType, props = {}) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.REGISTER,
        ...(currentPageName && { sourcePage: currentPageName }),
        ...props,
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackFamilyProgramEvent = (eventType, props = {}) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.FAMILY_PROGRAM,
        ...(currentPageName && { sourcePage: currentPageName }),
        ...props,
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackChatbotEvent = (eventType, props = {}) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.CHAT_BOT,
        ...(currentPageName && { sourcePage: currentPageName }),
        ...props,
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackCareHubEvent = (eventType, props = {}) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.CARE_HUB,
        ...(currentPageName && { sourcePage: currentPageName }),
        ...props,
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};

export const trackMoodSymptomHabitEvent = (eventType, props = {}) => {
  const currentPageName = getCurrentPage();
  try {
    trackEvent(
      eventType,
      withLocale({
        parent: MIXPANEL_EVENT_PARENT?.TRACKERS,
        ...(currentPageName && { sourcePage: currentPageName }),
        ...props,
      })
    );
  } catch (error) {
    console.error('Mixpanel tracking error:', error);
  }
};
